import {
  type CreateTeamPayload,
  type Team,
  type TeamRole,
  type TeamSearch,
} from "@repo/api";
import { assertUnreachable } from "@repo/utils";
import { type ActionType, createAction, getType } from "typesafe-actions";

type ManageTeamStep = "SELECT" | "DETAIL" | "CREATE";
type LoadingState = "CREATING" | "UPDATING" | "DELETING";

export interface ManageTeamState {
  currentStep: ManageTeamStep;
  teams: Team[];
  selectedTeamId?: number;
  updatedTeam?: Team;
  initialTeamMemberIds: string[];
  newTeam?: CreateTeamPayload;
  loadingState?: LoadingState;
  editMode: boolean;
  search?: TeamSearch;
  role?: TeamRole;
}

export const ManageTeamStateDefault: ManageTeamState = {
  currentStep: "SELECT",
  teams: [],
  selectedTeamId: undefined,
  updatedTeam: undefined,
  initialTeamMemberIds: [],
  newTeam: undefined,
  loadingState: undefined,
  editMode: false,
  search: undefined,
  role: undefined,
};

export type ManageTeamStateActions = typeof manageTeamStateActions;
export const manageTeamStateActions = {
  setStep: createAction("SET_STEP")<ManageTeamStep>(),
  setTeams: createAction("SET_TEAMS")<Team[]>(),

  setSelectedTeamId: createAction("SET_SELECTED_TEAM_ID")<number | undefined>(),
  setUpdatedTeam: createAction("SET_UPDATED_TEAM")<Team | undefined>(),
  setInitialTeamMemberIds: createAction("SET_INITIAL_TEAM_MEMBER_IDS")<
    string[]
  >(),
  setNewTeam: createAction("SET_NEW_TEAM")<CreateTeamPayload | undefined>(),
  setLoadingState: createAction("SET_LOADING_STATE")<
    LoadingState | undefined
  >(),
  setEditMode: createAction("SET_EDIT_MODE")<boolean>(),
  setSearch: createAction("SET_SEARCH")<TeamSearch | undefined>(),
  setRole: createAction("SET_ROLE")<TeamRole | undefined>(),
};

export function ManageTeamStateReducer(
  state: ManageTeamState,
  action: ActionType<ManageTeamStateActions>
) {
  switch (action.type) {
    case getType(manageTeamStateActions.setStep):
      return { ...state, currentStep: action.payload };
    case getType(manageTeamStateActions.setTeams):
      return { ...state, teams: action.payload };

    case getType(manageTeamStateActions.setSelectedTeamId):
      return { ...state, selectedTeamId: action.payload };
    case getType(manageTeamStateActions.setUpdatedTeam):
      return { ...state, updatedTeam: action.payload };
    case getType(manageTeamStateActions.setInitialTeamMemberIds):
      return { ...state, initialTeamMemberIds: action.payload };
    case getType(manageTeamStateActions.setNewTeam):
      return { ...state, newTeam: action.payload };
    case getType(manageTeamStateActions.setLoadingState):
      return { ...state, loadingState: action.payload };
    case getType(manageTeamStateActions.setEditMode):
      return { ...state, editMode: action.payload };
    case getType(manageTeamStateActions.setSearch):
      return { ...state, search: action.payload };
    case getType(manageTeamStateActions.setRole):
      return { ...state, role: action.payload };
  }
  assertUnreachable(action);
}
